// Third party components
import React, { useState, useRef, useMemo, useEffect } from 'react';
// import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { Stack, Typography } from '@mui/material';

// Shared components
import { Button, ContainerWithCard, LineLoader, SubHeader, PermissionFilter } from '@timmons-group/shared-react-components';
import { ACLS } from 'constants';

// CPP components
import MapInspector from 'components/MapInspector';
import MapLegend from 'components/MapLegend';
import MapSidebar, { SidebarItem } from 'components/MapSidebar';
import ThemeMap from 'components/ThemeMap';

// PAM components
import CommunityAreaModal from 'PAM/components/CommunityAreaModal';
import StatePreference from 'components/StatePreference';

// Hooks
import { useMapConfig } from '@timmons-group/shared-react-components/hooks';

const LAYER = 'communities:dashboard:communitiesMVT';

const Communities = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [statePref, setStatePref] = useState({});
  const [mapConfig, mapConfigLoading] = useMapConfig(
    'communities_planning_status'
  );
  const olMap = useRef(null);
  const oThemeMap = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [features, setFeatures] = useState([]);
  const [activeLayers, setActiveLayers] = useState([]);
  const sideBar = useRef(null);
  const themeMapInstance = useRef(null);

  useMemo(() => {
    if (!mapConfigLoading) {
      // TODO: FIX THIS hardcoding...maybe?
      const found = mapConfig?.layers?.find(
        (item) => item.key === LAYER
      );
      setActiveLayers(found ? [found.key] : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapConfigLoading]);

  // Set the page title on mount
  useEffect(() => {
    document.title = 'CPP - Communities';
  }, []);

  const setMap = (olm) => {
    oThemeMap.current = olm;
    // olMap is a ref to the map. So we need to get the .current property
    olMap.current = olm.olMap.current;
    setMapReady(true);
  };

  const processFeatures = (feats) => {
    if (sideBar.current) {
      sideBar.current.forceOpen();
      sideBar.current.select('inspector');
      setFeatures(feats);
    }
  };

  const clearFeatures = () => {
    setFeatures([]);
  }

  const updateSelection = (selection) => {
    setStatePref(selection);
  };

  const highlightFeature = (feature, highlight = true) => {
    if (themeMapInstance.current) {
      if (highlight) {
        themeMapInstance.current.highlightFeature(LAYER, feature);
      } else {
        themeMapInstance.current.unhighlightFeature(LAYER, feature);
      }
    }
  };

  const renderLegend = () => {
    if (mapReady) {
      return <MapLegend categories={olMap.current.themes?.categories} />;
    }

    return <LineLoader />;
  };

  const renderInspector = () => {
    if (mapReady) {
      return <MapInspector
        features={features}
        clear={clearFeatures}
        highlight={(feat) => highlightFeature(feat)}
        unhighlight={(feat) => highlightFeature(feat, false)}
      />;
    }

    return <LineLoader />;
  };

  const renderMap = () => {
    if (mapConfigLoading) {
      return <LineLoader message="Loading Map configuration" />;
    }
    return (
      <>
        {/* <SideBar open={true} /> */}
        <div className="map-bar">
          <MapSidebar ref={sideBar}>
            <SidebarItem id="legend" label="Legend">
              {renderLegend()}
            </SidebarItem>
            <SidebarItem id="inspector" label="Inspector">
              {renderInspector()}
            </SidebarItem>
          </MapSidebar>
          <ThemeMap
            ref={themeMapInstance}
            defaultExtent={statePref?.extent}
            themes={mapConfig}
            activeLayers={activeLayers}
            featuresClicked={processFeatures}
            // features={features}
            mapSet={setMap}
          />
        </div>
      </>
    );
  };

  const padding = {
    xs: '16px',
    lg: '7.8%',
  };

  return (
    <>
      <SubHeader
        title="Welcome to the Collaborative Planning Portal"
        rightRender={() => (
          <PermissionFilter all={[ACLS.ADD_PLANNING_AREA, ACLS.ADD_COMMUNITY]}>
            <Button
              label="+ Add Community"
              color="secondary"
              size="small"
              onClick={() => setIsOpen(!isOpen)}
            />
          </PermissionFilter>
        )}
      />
      <ContainerWithCard
        maxWidth={false}
        sx={{
          paddingLeft: padding,
          paddingRight: padding,
          marginTop: '16px',
        }}
      >
        <Stack
          spacing={2}
          direction="row"
          sx={{ minWidth: '100%', alignItems: 'center' }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="sectionHeader">
              Community Planning Areas
            </Typography>
          </Box>
          <Box sx={{ marginLeft: 'auto' }}>
            <StatePreference
              label="Zoom To"
              defaultLabel="My Region"
              update={updateSelection}
              isRequired={false}
            />
          </Box>
        </Stack>
        <Box sx={{ marginTop: '14px' }}>{renderMap()}</Box>
      </ContainerWithCard>
      {isOpen && <CommunityAreaModal modalClosed={() => setIsOpen(false)} />}
    </>
  );
};

export default Communities;
